import { useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  AreaSeries,
  CandlestickSeries,
  CrosshairMode,
  HistogramSeries,
  LineSeries,
  LineStyle,
  createChart,
} from 'lightweight-charts'
import type { ChartSpec } from '@shared/chart-spec'
import type { OhlcBar } from '@shared/types'
import { api } from '../../lib/api'
import { useStore } from '../../store'
import { ema, rsi, sma } from '../../lib/indicators'
import { fmtPct, fmtPrice } from '../../lib/format'
import { cn } from '../../lib/utils'

function cssVar(name: string, fallback = '#888'): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
}

const OVERLAY_INK = ['#9aa0a6', '#d2d5da', '#73767d', '#bcbfc6']
const LINE_STYLE: Record<string, LineStyle> = {
  solid: LineStyle.Solid,
  dashed: LineStyle.Dashed,
  dotted: LineStyle.Dotted,
}

// lightweight-charts renders the intraday time axis in UTC by default. These format it in
// ET (the US market's clock) so a 1D chart reads 9:30–16:00 like a US trader expects.
const ET_TIME = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: false })
const ET_DATE = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' })
const ET_DATETIME = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: false })

// EODHD's REST intraday is regular-session only; append the WS-recorded live bars (pre/after-
// market) that fall AFTER the last regular bar, so the chart shows the extended-hours session.
function mergeBars(eod: OhlcBar[], ws: OhlcBar[]): OhlcBar[] {
  if (!ws.length) return eod
  if (!eod.length) return ws
  const lastT = eod[eod.length - 1].time
  const tail = ws.filter((b) => b.time > lastT)
  return tail.length ? [...eod, ...tail] : eod
}

export function LightweightChart({
  spec,
  livePrice,
  hideHeader,
}: {
  spec: ChartSpec
  livePrice?: number | null
  hideHeader?: boolean
}) {
  const elId = `chart-${useId().replace(/:/g, '')}`
  const hostRef = useRef<HTMLDivElement>(null)
  const seriesRef = useRef<{ update: (bar: unknown) => void } | null>(null)
  const lastBarTimeRef = useRef(0) // last plotted bar time — the live-bars growth picks up after it
  const theme = useStore((s) => s.theme)
  const pointAt = useStore((s) => s.pointAt)
  const [bars, setBars] = useState<OhlcBar[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const hasRsi = useMemo(() => spec.panes?.some((p) => p.kind === 'rsi') ?? false, [spec.panes])
  const hasVol = useMemo(() => spec.panes?.some((p) => p.kind === 'volume') ?? false, [spec.panes])

  useEffect(() => {
    let alive = true
    setBars(null)
    setError(null)
    const intraday = ['1m', '5m', '15m', '1h'].includes(spec.timeframe)
    Promise.all([
      api.ohlc(spec.symbol, spec.timeframe, spec.range?.from, spec.range?.to),
      intraday ? api.liveBars(spec.symbol).catch(() => [] as OhlcBar[]) : Promise.resolve([] as OhlcBar[]),
    ])
      .then(([eod, ws]) => {
        if (!alive) return
        const merged = mergeBars(eod, ws)
        lastBarTimeRef.current = merged.length ? Number(merged[merged.length - 1].time) : 0
        setBars(merged)
      })
      .catch((e: Error) => alive && setError(e.message))
    return () => {
      alive = false
    }
  }, [spec.symbol, spec.timeframe, spec.range?.from, spec.range?.to])

  useEffect(() => {
    const host = hostRef.current
    if (!bars || bars.length === 0 || !host) return

    const fg = cssVar('--color-fg-subtle')
    const up = cssVar('--color-up', '#57b08a')
    const down = cssVar('--color-down', '#e0726b')
    const ink = cssVar('--color-fg', '#f3f4f6')
    const gridColor = theme === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.04)'
    const borderColor = theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.07)'
    const intraday = ['1m', '5m', '15m', '1h'].includes(spec.timeframe)

    const chart = createChart(host, {
      width: host.clientWidth,
      height: host.clientHeight,
      autoSize: false,
      layout: {
        background: { color: 'transparent' },
        textColor: fg,
        fontFamily: 'Geist Mono, ui-monospace, monospace',
        fontSize: 11,
        panes: { separatorColor: borderColor, separatorHoverColor: borderColor },
      },
      grid: { vertLines: { color: gridColor }, horzLines: { color: gridColor } },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: borderColor, labelBackgroundColor: cssVar('--color-surface-3') },
        horzLine: { color: borderColor, labelBackgroundColor: cssVar('--color-surface-3') },
      },
      rightPriceScale: { borderColor },
      timeScale: {
        borderColor,
        timeVisible: intraday,
        secondsVisible: false,
        // ET axis for intraday (time ticks → HH:MM ET, day-boundary ticks → "Jun 24").
        ...(intraday
          ? {
              tickMarkFormatter: (t: number, tickMarkType: number) =>
                tickMarkType >= 3 ? ET_TIME.format(new Date(t * 1000)) : ET_DATE.format(new Date(t * 1000)),
            }
          : {}),
      },
      // Crosshair tooltip time in ET too (intraday only; daily keeps the default date).
      ...(intraday ? { localization: { timeFormatter: (t: number) => ET_DATETIME.format(new Date(t * 1000)) } } : {}),
    })

    const toTime = (t: number | string) => t as never

    // main series
    let mainSeries
    if (spec.type === 'line') {
      mainSeries = chart.addSeries(LineSeries, { color: ink, lineWidth: 2 })
      mainSeries.setData(bars.map((b) => ({ time: toTime(b.time), value: b.close })))
    } else if (spec.type === 'area') {
      mainSeries = chart.addSeries(AreaSeries, {
        lineColor: ink,
        topColor: theme === 'light' ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.14)',
        bottomColor: 'rgba(255,255,255,0)',
        lineWidth: 2,
      })
      mainSeries.setData(bars.map((b) => ({ time: toTime(b.time), value: b.close })))
    } else {
      mainSeries = chart.addSeries(CandlestickSeries, {
        upColor: up,
        downColor: down,
        wickUpColor: up,
        wickDownColor: down,
        borderVisible: false,
      })
      mainSeries.setData(
        bars.map((b) => ({ time: toTime(b.time), open: b.open, high: b.high, low: b.low, close: b.close })),
      )
    }
    seriesRef.current = mainSeries as unknown as { update: (bar: unknown) => void }

    // overlays
    let ci = 0
    for (const ov of spec.overlays ?? []) {
      if (ov.kind === 'sma' || ov.kind === 'ema') {
        const data = ov.kind === 'sma' ? sma(bars, ov.period) : ema(bars, ov.period)
        const s = chart.addSeries(LineSeries, {
          color: ov.color || OVERLAY_INK[ci++ % OVERLAY_INK.length],
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        })
        s.setData(data.map((p) => ({ time: toTime(p.time), value: p.value })))
      } else if (ov.kind === 'hline') {
        mainSeries.createPriceLine({
          price: ov.price,
          color: ov.color || fg,
          lineWidth: 1,
          lineStyle: LINE_STYLE[ov.style ?? 'dashed'] ?? LineStyle.Dashed,
          axisLabelVisible: true,
          title: ov.label ?? '',
        })
      }
    }

    // volume overlay (anchored to bottom of main pane)
    if (hasVol) {
      const vol = chart.addSeries(HistogramSeries, {
        priceScaleId: 'vol',
        priceFormat: { type: 'volume' },
        color: 'rgba(150,150,160,0.4)',
      })
      vol.setData(
        bars.map((b) => ({
          time: toTime(b.time),
          value: b.volume ?? 0,
          color: b.close >= b.open ? 'rgba(87,176,138,0.40)' : 'rgba(224,114,107,0.40)',
        })),
      )
      chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } })
    }

    // RSI in its own pane
    if (hasRsi) {
      const p = spec.panes!.find((x) => x.kind === 'rsi') as { period?: number } | undefined
      const data = rsi(bars, p?.period ?? 14)
      const s = chart.addSeries(LineSeries, { color: cssVar('--color-accent'), lineWidth: 1, lastValueVisible: false }, 1)
      s.setData(data.map((pt) => ({ time: toTime(pt.time), value: pt.value })))
      try {
        const panes = chart.panes()
        panes[0]?.setStretchFactor(3)
        panes[1]?.setStretchFactor(1)
      } catch {
        /* pane stretch is best-effort */
      }
    }

    chart.timeScale().fitContent()

    const first = bars[0]?.close ?? 0
    const last = bars[bars.length - 1]?.close ?? 0
    const tone = last > first ? 'up' : last < first ? 'down' : 'neutral'
    // Orb only points on desktop — on mobile it would crowd the small screen.
    const pt = setTimeout(() => {
      if (window.innerWidth >= 768) pointAt(elId, tone)
    }, 360)

    const ro = new ResizeObserver(() => {
      chart.applyOptions({ width: host.clientWidth, height: host.clientHeight })
    })
    ro.observe(host)

    return () => {
      clearTimeout(pt)
      ro.disconnect()
      chart.remove()
      seriesRef.current = null
    }
  }, [bars, theme, spec, elId, pointAt, hasRsi, hasVol])

  // Live tip for DAILY charts (1M/1Y/…): extend with a trailing point at today's live price.
  // Intraday pre/after-market is handled by real WS bars (the growth effect below), not this.
  useEffect(() => {
    const s = seriesRef.current
    if (!s || livePrice == null || !Number.isFinite(livePrice) || !bars || !bars.length) return
    const tf = spec.timeframe
    const intraday = ['1m', '5m', '15m', '1h'].includes(tf)
    const lastBar = bars[bars.length - 1]
    let time: number | string
    if (intraday) {
      return // extended-hours now comes from real WS bars
    } else if (tf === '1D') {
      const today = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(new Date())
      time = today > String(lastBar.time) ? today : String(lastBar.time)
    } else {
      return // weekly/monthly: a single live daily point would be granularity-mismatched
    }
    try {
      if (spec.type === 'candlestick') {
        const o = lastBar.close
        s.update({ time, open: o, high: Math.max(o, livePrice), low: Math.min(o, livePrice), close: livePrice })
      } else {
        s.update({ time, value: livePrice })
      }
    } catch {
      /* lightweight-charts rejects out-of-order times — ignore this tick */
    }
  }, [livePrice, bars, spec.timeframe, spec.type])

  // Live growth: for intraday charts, poll the WS-recorded bars and append new ones (the
  // pre/after-market session as it develops) straight onto the series — no chart rebuild.
  useEffect(() => {
    if (!['1m', '5m', '15m', '1h'].includes(spec.timeframe)) return
    let alive = true
    const tick = () =>
      api
        .liveBars(spec.symbol)
        .then((ws) => {
          const s = seriesRef.current
          if (!alive || !s) return
          for (const b of ws) {
            if (Number(b.time) <= lastBarTimeRef.current) continue
            try {
              if (spec.type === 'candlestick') s.update({ time: b.time, open: b.open, high: b.high, low: b.low, close: b.close })
              else s.update({ time: b.time, value: b.close })
              lastBarTimeRef.current = Number(b.time)
            } catch {
              /* out-of-order — skip */
            }
          }
        })
        .catch(() => {})
    const id = setInterval(tick, 15_000)
    return () => {
      alive = false
      clearInterval(id)
    }
  }, [spec.symbol, spec.timeframe, spec.type])

  const periodReturn = useMemo(() => {
    if (!bars || bars.length < 2) return null
    const a = bars[0].close
    const b = bars[bars.length - 1].close
    return ((b - a) / a) * 100
  }, [bars])

  const height = 300 + (hasRsi ? 120 : 0)
  const last = bars && bars.length ? bars[bars.length - 1].close : null

  return (
    <div id={elId} className="card my-3 overflow-hidden animate-rise">
      {!hideHeader && (
        <div className="flex items-center justify-between gap-3 border-b border-[var(--line-soft)] px-3.5 py-2.5">
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-fg">
              {spec.title || spec.symbol}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-fg-subtle">
              <span className="num">{spec.symbol}</span>
              <span className="rounded bg-[var(--color-surface-3)] px-1.5 py-px text-[10px] uppercase tracking-wide">
                {spec.timeframe}
              </span>
              <span className="capitalize">{spec.type}</span>
            </div>
          </div>
          {last != null && (
            <div className="text-right">
              <div className="num text-[15px] font-semibold text-fg">{fmtPrice(last)}</div>
              {periodReturn != null && (
                <div
                  className={cn(
                    'num text-[11px]',
                    periodReturn > 0 ? 'text-up' : periodReturn < 0 ? 'text-down' : 'text-flat',
                  )}
                >
                  {fmtPct(periodReturn)} period
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="relative">
        {error ? (
          <div className="flex h-[180px] items-center justify-center px-4 text-center text-[12px] text-down">
            Couldn’t load {spec.symbol}: {error}
          </div>
        ) : !bars ? (
          <div className="flex h-[220px] items-center justify-center text-[12px] text-fg-subtle">
            <span className="animate-pulse">Loading {spec.symbol}…</span>
          </div>
        ) : bars.length === 0 ? (
          <div className="flex h-[180px] items-center justify-center text-[12px] text-fg-subtle">
            No data for {spec.symbol} ({spec.timeframe}).
          </div>
        ) : (
          <div ref={hostRef} style={{ height }} className="w-full" />
        )}
      </div>
    </div>
  )
}
